import React, { useState, useEffect, useCallback } from "react";
import {
  Download, Image, Loader2, ChevronLeft, ChevronRight,
  Filter, Package, Calendar, X, Check, Eye
} from "lucide-react";
import AIImageWithDisclaimer from "./AIImageWithDisclaimer";
import { bakeDisclaimerOnImage } from "../utils/aiDisclaimer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIGalleryTab = ({ authAxios }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [packages, setPackages] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [packageFilter, setPackageFilter] = useState("");
  const [occasionFilter, setOccasionFilter] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (packageFilter) params.append("package", packageFilter);
      if (occasionFilter) params.append("occasion", occasionFilter);
      const r = await authAxios.get(`${API_URL}/api/admin/ai-gallery?${params}`);
      setImages(r.data.images || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPackages(r.data.packages || []);
      setOccasions(r.data.occasions || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios, page, packageFilter, occasionFilter]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const getImageSrc = (b64) => {
    if (!b64) return "";
    if (b64.startsWith("data:")) return b64;
    return `data:image/jpeg;base64,${b64}`;
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (packageFilter) params.append("package", packageFilter);
      if (occasionFilter) params.append("occasion", occasionFilter);
      const r = await authAxios.get(`${API_URL}/api/admin/ai-gallery/download-all?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DoozyGo_AI_Gallery.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
    setDownloading(false);
  };

  const handleDownloadSingle = async (img) => {
    const b64 = img.image_base64;
    const src = b64.includes(",") ? b64 : `data:image/jpeg;base64,${b64}`;
    // Bake the AI disclaimer onto the exported bitmap so it travels with
    // the downloaded file.
    const dataUrl = await bakeDisclaimerOnImage(src, { mime: "image/jpeg" });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `DoozyGo_${img.package}_${img.occasion}_${img.job_id}.jpg`;
    a.click();
  };

  const handleDownloadSelected = () => {
    const selectedImages = images.filter(img => selected.has(img.job_id));
    selectedImages.forEach((img, i) => {
      setTimeout(() => handleDownloadSingle(img), i * 300);
    });
  };

  const toggleSelect = (jobId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === images.length) setSelected(new Set());
    else setSelected(new Set(images.map(i => i.job_id)));
  };

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return d.slice(0, 10); }
  };

  const pkgColor = (pkg) => {
    const m = { Basic: "bg-emerald-100 text-emerald-700", Classic: "bg-blue-100 text-blue-700", Premium: "bg-purple-100 text-purple-700", Luxury: "bg-amber-100 text-amber-700" };
    return m[pkg] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-5" data-testid="ai-gallery">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#1E1B4B]">AI Generated Gallery</h2>
          <p className="text-xs text-[#94a3b8]">{total} images generated via DoozyGo Magic</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selected.size > 0 && (
            <button onClick={handleDownloadSelected} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-[#1E1B4B] text-white hover:bg-[#1E1B4B]/90 transition-colors" data-testid="download-selected-btn">
              <Download size={13} /> Download {selected.size} Selected
            </button>
          )}
          <button onClick={handleDownloadAll} disabled={downloading || total === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gradient-to-r from-[#E83E8C] to-[#F97316] text-white hover:opacity-90 disabled:opacity-50 transition-colors"
            data-testid="download-all-btn">
            {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {downloading ? "Zipping..." : `Download All (${total})`}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter size={12} className="text-[#94a3b8]" />
          <select value={packageFilter} onChange={e => { setPackageFilter(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-[#475569] focus:ring-1 focus:ring-[#E83E8C]/30 outline-none"
            data-testid="filter-package">
            <option value="">All Packages</option>
            {packages.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar size={12} className="text-[#94a3b8]" />
          <select value={occasionFilter} onChange={e => { setOccasionFilter(e.target.value); setPage(1); }}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-[#475569] focus:ring-1 focus:ring-[#E83E8C]/30 outline-none"
            data-testid="filter-occasion">
            <option value="">All Occasions</option>
            {occasions.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <button onClick={selectAll} className="flex items-center gap-1 text-xs text-[#475569] hover:text-[#E83E8C] transition-colors px-2 py-1 rounded-lg border border-gray-200 bg-white"
          data-testid="select-all-btn">
          {selected.size === images.length && images.length > 0 ? <X size={11} /> : <Check size={11} />}
          {selected.size === images.length && images.length > 0 ? "Deselect All" : "Select All"}
        </button>
      </div>

      {/* Gallery Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#E83E8C]" size={28} /></div>
      ) : images.length === 0 ? (
        <div className="text-center py-20">
          <Image size={40} className="text-[#E83E8C]/30 mx-auto mb-3" />
          <p className="text-sm text-[#94a3b8]">No AI-generated images found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="gallery-grid">
          {images.map((img) => (
            <div key={img.job_id}
              className={`group relative bg-white rounded-xl overflow-hidden border-2 transition-all duration-200 hover:shadow-lg cursor-pointer ${selected.has(img.job_id) ? "border-[#E83E8C] shadow-md" : "border-gray-100"}`}
              data-testid={`gallery-item-${img.job_id}`}>
              {/* Selection checkbox */}
              <button onClick={(e) => { e.stopPropagation(); toggleSelect(img.job_id); }}
                className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-md flex items-center justify-center transition-all ${selected.has(img.job_id) ? "bg-[#E83E8C] text-white" : "bg-white/80 border border-gray-300 opacity-0 group-hover:opacity-100"}`}>
                {selected.has(img.job_id) && <Check size={10} strokeWidth={3} />}
              </button>

              {/* Image */}
              <div className="aspect-[4/5] overflow-hidden relative" onClick={() => setLightbox(img)}>
                <img src={getImageSrc(img.image_base64)} alt={`${img.package} ${img.occasion}`} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 px-1.5 py-0.5 text-[7px] sm:text-[8px] font-medium text-white text-center leading-tight bg-black/55 backdrop-blur-[2px]" data-testid="ai-image-disclaimer">
                  AI-generated reference image. T&amp;C apply.
                </div>
              </div>

              {/* Info overlay */}
              <div className="p-2.5">
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${pkgColor(img.package)}`}>{img.package}</span>
                  <span className="text-[9px] text-[#94a3b8]">{img.occasion}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[8px] text-[#94a3b8]">{formatDate(img.created_at)}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setLightbox(img); }}
                      className="p-1 rounded-md hover:bg-gray-100 text-[#94a3b8] hover:text-[#1E1B4B] transition-colors" title="View">
                      <Eye size={11} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDownloadSingle(img); }}
                      className="p-1 rounded-md hover:bg-gray-100 text-[#94a3b8] hover:text-[#E83E8C] transition-colors" title="Download">
                      <Download size={11} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50 transition-colors" data-testid="prev-page-btn">
            <ChevronLeft size={13} /> Prev
          </button>
          <span className="text-xs text-[#475569]">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-gray-200 bg-white disabled:opacity-40 hover:bg-gray-50 transition-colors" data-testid="next-page-btn">
            Next <ChevronRight size={13} />
          </button>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox-overlay">
          <div className="relative max-w-3xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-white/70 hover:text-white">
              <X size={24} />
            </button>
            <AIImageWithDisclaimer src={getImageSrc(lightbox.image_base64)} alt={`${lightbox.package} ${lightbox.occasion}`}
              wrapperClassName="w-full"
              className="w-full max-h-[80vh] object-contain rounded-xl" />
            <div className="flex items-center justify-between mt-3 px-1">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pkgColor(lightbox.package)}`}>{lightbox.package}</span>
                <span className="text-xs text-white/70">{lightbox.occasion}</span>
                <span className="text-xs text-white/50">{formatDate(lightbox.created_at)}</span>
              </div>
              <button onClick={() => handleDownloadSingle(lightbox)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-[#1E1B4B] hover:bg-gray-100 transition-colors">
                <Download size={13} /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIGalleryTab;
